import { useRef, useState } from "react";
import { DataTable, DataTableRowEditCompleteEvent } from "primereact/datatable";
import { Column, ColumnEditorOptions } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import UserAddressService from "../../services/useraddress/useraddress.service";
import { Toast, ToastMessage } from "primereact/toast";
import { Dialog } from "primereact/dialog";
import { PincodeAreaModel } from "../../models/cust-address/cust-address-model";
 
interface DataTableProps {
    data: any[];
    NoofRecords: number;
    pageNumber: number;
    pageSize: number;
    searchTerm: any;
    onPageChange: any;
    onDataUpdate: (updatedData: any[]) => void;
}
 
const StateDataTable: React.FC<DataTableProps> = (props) => {
    const toast = useRef<Toast>(null);
    const [selectedState, setSelectedState] = useState<PincodeAreaModel | null>(null);
    const [showDeleteDialog, setShowDeleteDialog] = useState(false);
 
    const showMessage = (
        label: string,
        message: string,
        severity: ToastMessage["severity"]
    ) => {
        toast.current?.show({
            severity,
            summary: label,
            detail: message,
            life: 3000,
        });
    };
 
    const stateEditor = (options: ColumnEditorOptions) => {
        return (
            <InputText
                type="text"
                value={options.value}
                style={{ width: "95%", border: "1px solid #090" }}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    options.editorCallback!(e.target.value)
                }
            />
        );
    };
 
    const onRowEditComplete = async (event: DataTableRowEditCompleteEvent) => {
        const { newData } = event;
        const _data = [...props.data];
   
        const updatedIndex = _data.findIndex(item => item.stateId === newData.stateId);
        if (!newData.stateName || newData.stateName.trim() === "") {
            showMessage("Validation Error", "State name cannot be empty", "warn");
            props.onDataUpdate([..._data]);  
            return;
        }
   
        if (updatedIndex !== -1 && _data[updatedIndex].stateName !== newData.stateName) {
            try {
                await UserAddressService.updateState(newData.stateId, newData.stateName);
                _data[updatedIndex] = { ..._data[updatedIndex], ...newData };
                props.onDataUpdate(_data);
                showMessage("Success", "State updated successfully", "success");
            } catch (error) {
                showMessage("Error", "Failed to update state", "error");
                console.error("Error updating state:", error);
            }
        }
    };
 
    const deleteTemplate = (rowData: PincodeAreaModel) => {
        return (
            <Button
                icon="pi pi-trash"
                onClick={() => confirmDelete(rowData)}
                style={{ border: 'none', color: 'gray', background: 'none' }}
                className="p-button-rounded"
            />
        );
    };
 
    const confirmDelete = (rowData: PincodeAreaModel) => {
        setSelectedState(rowData);
        setShowDeleteDialog(true);
    };
 
    const deleteState = async () => {
        if (selectedState) {
            try {
                const response = await UserAddressService.deletestate(selectedState.stateId);
 
                if (response && response.isSuccess) {
                    showMessage("Success", "State deleted successfully", "success");
 
                    const updatedData = props.data.filter(
                        (state) => state.stateId !== selectedState.stateId
                    );
 
                    const filteredData = updatedData.filter(
                        (state) => state.IsDelete !== true
                    );
 
                    props.onDataUpdate(filteredData);
 
                    props.onPageChange({
                        first: (props.pageNumber - 1) * props.pageSize,
                        rows: props.pageSize,
                        page: props.pageNumber - 1,
                    });
                } else {
                    showMessage("Error", response?.message || "Failed to delete state", "error");
                }
            } catch (error) {
                showMessage("Error", "Failed to delete state", "error");
                console.error("Error deleting state:", error);
            } finally {
                setShowDeleteDialog(false);
                setSelectedState(null);
            }
        }
    };
 
    const onHideDeleteDialog = () => {
        setSelectedState(null);
        setShowDeleteDialog(false);
    };
 
    const deleteDialogFooter = (
        <div>
            <Button label="Cancel" icon="pi pi-times" onClick={onHideDeleteDialog} className="p-button-text" />
            <Button label="Delete" icon="pi pi-check" onClick={deleteState} className="p-button-danger" />
        </div>
    );
 
    return (
        <div className="col-12">
            <Toast ref={toast} />
            <div className="card border-noround p-2">
                <DataTable
                    value={props.data}
                    dataKey="stateId"
                    paginator
                    editMode="row"
                    rows={props.pageSize}
                    rowsPerPageOptions={[5, 10, 25, 50, 100]}
                    totalRecords={props.NoofRecords}
                    lazy={true}
                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                    currentPageReportTemplate={`{first} to {last} of ` + props.NoofRecords}
                    first={(props.pageNumber - 1) * props.pageSize}
                    onPage={props.onPageChange}
                    onRowEditComplete={onRowEditComplete}
                >
                    <Column field="stateName" header="State" editor={stateEditor} style={{ width: "25%" }} className="pl-6" />
                    <Column field="countryName" header="Country" style={{ width: "15%" }} />
                    <Column rowEditor header="Action" style={{ width: "5%" }} />
                    <Column body={deleteTemplate} header="Delete" style={{ width: "5%" }} />
                </DataTable>
            </div>
 
            <Dialog
                visible={showDeleteDialog}
                style={{ width: '400px' }}
                header="Confirm Delete"
                modal
                footer={deleteDialogFooter}
                onHide={onHideDeleteDialog}
            >
                <p>Are you sure you want to delete the state <b>{selectedState?.stateName}</b>?</p>
            </Dialog>
        </div>
    );
};
 
export default StateDataTable;
 
 